import type {
    ColDef,
    GridOptions,
    SendToClipboardParams,
    SideBarDef,
    StateUpdatedEvent,
} from '@ag-grid-community/core';
import type { AgGridReact } from '@ag-grid-community/react';
import type { ForwardedRef, Ref } from 'react';
import { useCallback, useMemo } from 'react';
import { writeClipboardText } from '@components/ICSGrid/clipboard';
import {
    DEFAULT_PAGINATION_PAGE_SIZE,
    defaultConfiguration,
} from '@components/ICSGrid/config';
import CustomEmptyState from '@components/ICSGrid/CustomEmptyState';
import { useGridColumnState } from '@components/ICSGrid/use-grid-column-state';
import { useToast } from '@contexts/GlobalUiContext';

interface UseTableSettings {
    commonGridOptions: Partial<GridOptions>;
}

export default function useTableSettings(
    ref: Ref<AgGridReact | null> | ForwardedRef<AgGridReact | null>,
    id: string,
): UseTableSettings {
    const { pushSuccessToast } = useToast();

    const { saveColumnsState, getColumnsState } = useGridColumnState(id);

    const sideBar: SideBarDef = useMemo(
        () => ({
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                    toolPanelParams: {
                        suppressRowGroups: true,
                        suppressValues: true,
                        suppressPivots: true,
                        suppressPivotMode: true,
                    },
                },
            ],
            position: 'right',
            defaultToolPanel: '',
            hiddenByDefault: false,
        }),
        [],
    );
    const handleStateUpdate = useCallback(
        (event: StateUpdatedEvent) => {
            saveColumnsState(event.state);
        },
        [saveColumnsState],
    );

    const initialState = useMemo(() => {
        return getColumnsState();
    }, [getColumnsState]);

    const selectionColumnDef: ColDef = {
        pinned: 'left',
        width: 36,
        minWidth: 36,
        maxWidth: 36,
        colId: 'selection',
        autoHeight: true,
        cellClass: 'ag-selection-cell',
        headerClass: 'ag-selection-header',
        lockVisible: true,
        suppressColumnsToolPanel: true,
    };

    /**
     * Overwrite the built in copy to clipboard functionality
     * in order to display a snackbar message
     */
    function sendToClipboard(params: SendToClipboardParams): void {
        void writeClipboardText(params.data).then(() => {
            pushSuccessToast({
                detail: 'Copied to clipboard',
            });
        });
    }

    const commonGridOptions: Partial<GridOptions> = {
        ...defaultConfiguration,
        paginationPageSize: DEFAULT_PAGINATION_PAGE_SIZE,
        initialState,
        selectionColumnDef,
        sideBar,
        noRowsOverlayComponent: CustomEmptyState,
        sendToClipboard,
        onStateUpdated: handleStateUpdate,
    };

    return {
        commonGridOptions,
    };
}
